import { useEffect, useMemo, useRef, useState } from "react";
import client from "../api/client";

const buildQuery = ({ query, mode, context, experienceCategory, limit }) => {
  const params = new URLSearchParams();
  if (query) params.set("q", query);
  if (mode) params.set("mode", mode);
  if (context?.type) params.set("contextType", context.type);
  if (context?.district) params.set("district", context.district);
  if (context?.destinationSlug) params.set("destinationSlug", context.destinationSlug);
  if (experienceCategory) params.set("category", experienceCategory);
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export function useLocationSearchRemote({
  query = "",
  mode = "all",
  context = { type: "all" },
  experienceCategory = "",
  limit = 20,
  enabled = true,
  debounceMs = 180,
}) {
  const [state, setState] = useState({
    status: "idle",
    sections: [],
    error: null,
  });
  const latestRef = useRef(0);

  const key = useMemo(
    () =>
      JSON.stringify({
        query,
        mode,
        context,
        experienceCategory,
        limit,
        enabled,
      }),
    [query, mode, context, experienceCategory, limit, enabled],
  );

  useEffect(() => {
    if (!enabled) {
      setState((prev) => ({ ...prev, status: "idle", error: null }));
      return;
    }

    const now = Date.now();
    latestRef.current = now;
    setState((prev) => ({
      status: "loading",
      sections: Array.isArray(prev.sections) ? prev.sections : [],
      error: null,
    }));
    const normalizedQuery = String(query || "").trim();

    const controller = new AbortController();
    const timer = setTimeout(() => {
      const requestId = latestRef.current;
      client
        .get(`/places/search${buildQuery({ query, mode, context, experienceCategory, limit })}`, {
          signal: controller.signal,
        })
        .then((payload) => {
          if (latestRef.current !== requestId) return;
          const responseQuery =
            typeof payload?.query === "string" ? payload.query.trim() : normalizedQuery;
          if (responseQuery !== normalizedQuery) return;
          setState({ status: "success", sections: payload?.sections || [], error: null });
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          if (latestRef.current !== requestId) return;
          setState({ status: "error", sections: [], error });
        });
    }, debounceMs);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [key, query, mode, context, experienceCategory, limit, enabled, debounceMs]);

  return {
    sections: state.sections,
    status: state.status,
    error: state.error,
    isLoading: state.status === "loading",
  };
}
